import { useCallback, useEffect, useMemo, useState } from "react";
import { BrainCircuit, CheckCircle2, DatabaseZap, FileSearch, Play, RefreshCw, ShieldCheck, Sparkles } from "lucide-react";
import { ModuleGuide, useModuleCopy } from "./ModuleGuide";

import {
  compileContext,
  inspectContext,
  loadWorkflowRuns,
  runStoryWorkflow,
  type ContextPackInspection,
  type StoryWorkflowRun,
  type StudioAuth,
} from "../api/client";

type WorkflowKey = "story.scene.draft.v1" | "story.scene.revise.v1" | "story.continuity.evaluate.v1" | "story.research.synthesize.v1";

type ContextAiPanelProps = {
  apiOrigin: string;
  projectId: string;
  unitId: string;
  unitTitle: string;
  auth: StudioAuth;
  onProposal: () => Promise<void>;
};

function countLayer(layer: unknown): number {
  if (Array.isArray(layer)) return layer.length;
  if (layer && typeof layer === "object") return Object.values(layer).reduce((total, item) => total + countLayer(item), 0);
  return layer === null || layer === undefined ? 0 : 1;
}

export function ContextAiPanel({ apiOrigin, projectId, unitId, unitTitle, auth, onProposal }: ContextAiPanelProps) {
  const [pack, setPack] = useState<ContextPackInspection | null>(null);
  const [runs, setRuns] = useState<StoryWorkflowRun[]>([]);
  const [instruction, setInstruction] = useState("");
  const copy = useModuleCopy();
  const [completed, setCompleted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const revisions = useMemo(() => runs.filter((run) => run.unit_id === unitId && run.workflow_key === "story.scene.revise.v1").length, [runs, unitId]);

  const refreshRuns = useCallback(async () => {
    setRuns(await loadWorkflowRuns({ apiOrigin, projectId, auth }));
  }, [apiOrigin, auth, projectId]);

  useEffect(() => { void refreshRuns().catch((cause: unknown) => setError(cause instanceof Error ? cause.message : "Workflow history unavailable")); }, [refreshRuns]);

  async function buildContext(taskType: WorkflowKey = "story.scene.draft.v1") {
    setBusy(true);
    setError(null);
    try {
      const created = await compileContext({ apiOrigin, projectId, unitId, taskType, auth });
      setPack(await inspectContext({ apiOrigin, projectId, contextPackId: created.id, auth }));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "ContextPack 编译失败。");
    } finally {
      setBusy(false);
    }
  }

  async function run(key: WorkflowKey) {
    if (busy || !instruction.trim()) return;
    setCompleted(false);
    setBusy(true);
    setError(null);
    try {
      const result = await runStoryWorkflow({
        apiOrigin,
        projectId,
        unitId,
        workflowKey: key,
        instruction,
        revisionCount: key === "story.scene.revise.v1" ? revisions : 0,
        auth,
      });
      setPack(await inspectContext({ apiOrigin, projectId, contextPackId: result.contextPackId, auth }));
      await refreshRuns();
      if (result.pendingChangeId) await onProposal();
      setCompleted(true);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Story Workflow 运行失败。");
    } finally {
      setBusy(false);
    }
  }

  const layers = pack ? ((pack.deterministicContext.layers ?? {}) as Record<string, unknown>) : {};
  return (
    <main className="context-ai-panel" aria-label="上下文与 AI 工作流">
      <header className="context-ai-header">
        <div><span className="eyebrow">CONTEXT COMPILER V3</span><h1>上下文与 AI</h1><p>{unitTitle}</p></div>
        <button type="button" onClick={() => { void buildContext(); }} disabled={busy}><RefreshCw size={16} />编译 ContextPack</button>
      </header>
      <ModuleGuide module="context" />
      <div className="context-ai-grid">
        <section className="context-card workflow-console">
          <div className="context-card-title"><Sparkles /><div><h2>Story Workflow</h2><p>模型只提出变更；接受 Canon 的按钮仍在作者手里。</p></div></div>
          <label>作者指令<textarea disabled={busy} placeholder={copy.instruction} value={instruction} onChange={(event) => setInstruction(event.target.value)} maxLength={8000} /></label>
          {busy ? <p role="status">{copy.working}</p> : completed ? <p role="status">{copy.done}</p> : null}
          {revisions >= 2 ? <p>{copy.revisions}</p> : null}
          <div className="workflow-actions">
            <button type="button" disabled={busy || !instruction.trim()} onClick={() => { void run("story.scene.draft.v1"); }}><Play size={15} />生成场景提案</button>
            <button type="button" disabled={busy || !instruction.trim() || revisions >= 2} onClick={() => { void run("story.scene.revise.v1"); }}><BrainCircuit size={15} />有界修订 {revisions}/2</button>
            <button type="button" disabled={busy || !instruction.trim()} onClick={() => { void run("story.continuity.evaluate.v1"); }}><ShieldCheck size={15} />连续性评估</button>
          </div>
          {error ? <p className="inline-error" role="alert">{error}</p> : null}
          <div className="workflow-contract"><CheckCircle2 /><span>PendingChange only</span><CheckCircle2 /><span>Private payload</span><CheckCircle2 /><span>Human acceptance</span></div>
        </section>
        <details className="context-card inspector-console module-advanced"><summary>{copy.advanced}</summary>
          <div className="context-card-title"><DatabaseZap /><div><h2>Context Inspector</h2><p>{pack ? `${pack.compilerVersion} · ${pack.rankingProfile} · ${pack.usedTokens}/${pack.tokenBudget} estimated tokens` : "尚未编译当前场景"}</p></div></div>
          {pack ? <>
            <div className="workflow-contract"><span>{pack.candidateCount} 候选</span><span>{pack.selectedCount} 纳入</span><span>{pack.truncatedCount} 截断</span><span>{pack.compileLatencyMs} ms</span></div>
            <div className="layer-list">
              {Object.entries(layers).map(([name, value]) => <article key={name}><span>{name}</span><strong>{countLayer(value)}</strong><small>items</small></article>)}
            </div>
            <div className="retrieval-list">
              <h3><FileSearch size={16} />检索决策</h3>
              {pack.retrievalAudit.map((item) => <div key={String(item.id)}><strong>{String(item.source_type)}</strong><p>{String(item.reason)}</p><span>{String(item.token_cost)} tok · {String(item.decision)}</span></div>)}
            </div>
          </> : <button className="empty-context" type="button" onClick={() => { void buildContext(); }}>编译后查看 Hard / Temporal / POV / Graph / Semantic / External 的纳入与截断原因</button>}
        </details>
        <section className="context-card run-ledger">
          <div className="context-card-title"><BrainCircuit /><div><h2>运行与评估</h2><p>跨服务 run ID、ContextPack 与 PendingChange 全链路可追溯。</p></div></div>
          {runs.length > 0 ? runs.slice(0, 8).map((runItem) => <article key={runItem.id}><div><strong>{runItem.workflow_key}</strong><small>{runItem.external_run_id ?? "等待外部 run"}</small></div><span className={`run-status is-${runItem.status}`}>{runItem.status}</span><time>{new Date(runItem.created_at_ms).toLocaleString("zh-CN")}</time></article>) : <p className="empty-world">当前分支还没有 AI Workflow 运行。</p>}
        </section>
      </div>
    </main>
  );
}
